import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { isFile, deleteData, deleteImage } from '../utils/functions';
import './ImageUpload.css';
import { useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api/';

const ImageUpload = ({ itemFormData, setItemFormData, originalItemData = { extra_images: [], featured_image: null } }) => {
  useEffect(() => {
    const objectURLs = itemFormData.extra_images
      .filter(isFile)
      .map((image) => URL.createObjectURL(image));

    return () => {
      objectURLs.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [itemFormData.extra_images]);

  const onDrop = (acceptedFiles) => {
    const validImages = acceptedFiles.filter((file) => file.type.startsWith('image/'));

    setItemFormData((prevData) => {
      const updatedExtraImages = [...prevData.extra_images, ...validImages];
      let updatedFeaturedImage = prevData.featured_image;

      if (!updatedFeaturedImage && validImages.length > 0) {
        updatedFeaturedImage = validImages[0];
        updatedExtraImages.shift();
      }

      return {
        ...prevData,
        featured_image: updatedFeaturedImage,
        extra_images: updatedExtraImages,
      };
    });
  };

  const removeImage = async (index) => {
    const imageToDelete = index === -1 ? itemFormData.featured_image : itemFormData.extra_images[index];
  
    if (isFile(imageToDelete)) {
      updateItemFormData(index);
    } else {
      if (window.confirm('Are you sure you want to delete this image?')) {
        try {
          if (originalItemData.extra_images.includes(imageToDelete)) {
            await deleteData('extra-image', imageToDelete.id);
          } else {
            await deleteData('featured-image', imageToDelete.id);
          }
          await deleteImage(imageToDelete.image);
          updateItemFormData(index);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      }
    }
  };

  const updateItemFormData = (index) => {
    setItemFormData((prevData) => {
      const updatedImages = [...prevData.extra_images];
      const updatedFeaturedImage = index === -1 ? null : prevData.featured_image;

      if (index >= 0) {
        updatedImages.splice(index, 1);
      }

      return {
        ...prevData,
        extra_images: updatedImages,
        featured_image: updatedFeaturedImage,
      };
    });
  };

  const handleFeaturedImageSelection = (index) => {
    setItemFormData((prevData) => {
      const newFeaturedImage = prevData.extra_images[index];
      const updatedExtraImages = prevData.extra_images.filter((_, i) => i !== index);

      if (prevData.featured_image) {
        updatedExtraImages.push(prevData.featured_image);
      }

      return {
        ...prevData,
        extra_images: updatedExtraImages,
        featured_image: newFeaturedImage,
      };
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/*',
  });

  return (
    <div className="container">
      {itemFormData.featured_image && (
        <div>
          <h3>Featured Image:</h3>
          <div>
            <img
              src={
                isFile(itemFormData.featured_image)
                  ? URL.createObjectURL(itemFormData.featured_image)
                  : `${API_URL}image/${itemFormData.featured_image.image}`
              }
              alt={itemFormData.featured_image.image || 'Featured image'}
              className="featured-image"
            />
          </div>
          {isFile(itemFormData.featured_image) && (
            <button onClick={() => removeImage(-1)}>Remove</button>
          )}
        </div>
      )}
      <div className="images-container">
        {itemFormData.extra_images.map((image, index) => (
          <div key={index} className="image-wrapper">
            <img
              src={
                isFile(image)
                  ? URL.createObjectURL(image)
                  : `${API_URL}image/${image.image}`
              }
              alt={image.image || `Image ${index}`}
              className="image"
              onClick={() => handleFeaturedImageSelection(index)}
            />
            <button onClick={() => removeImage(index)}>
              {isFile(image) ? 'Remove' : 'Delete'}
            </button>
          </div>
        ))}
      </div>
      {!itemFormData.featured_image && itemFormData.extra_images.length > 0 && (
        <div className="error">Select an image to be your featured image</div>
      )}
      <div {...getRootProps()} className="dropzone" role="button" aria-label="Upload images">
        <input {...getInputProps()} aria-hidden="true" />
        <p>{`Drag 'n' drop some images here, or click to select files`}</p>
      </div>
      {itemFormData.extra_images.length === 0 && (
        <div className="alert">Upload at least one image</div>
      )}
    </div>
  );
};

ImageUpload.propTypes = {
  itemFormData: PropTypes.shape({
    featured_image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    extra_images: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ),
  }).isRequired,
  setItemFormData: PropTypes.func.isRequired,
  originalItemData: PropTypes.shape({
    extra_images: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ),
    featured_image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
};

export default ImageUpload;
